import React, {
  ChangeEvent,
  CSSProperties,
  Dispatch,
  FocusEventHandler,
  RefObject,
  useState
} from 'react';
import {
  alpha,
  Box,
  FormControl,
  FormHelperText,
  InputBase,
  InputLabel,
  Stack,
  styled,
  Chip
} from '@mui/material';
import { theme } from '../../../ThemeContext/ThemeObject';
import { sharedClasses } from './sharedClasses';
import DebounceInput from '../Utilities/DebounceInput';

type IStyle = {
  [className: string]: CSSProperties | IStyle;
};

interface IFormTextFieldProps {
  label?: string;
  value: string | number;
  onChange: Dispatch<ChangeEvent<HTMLInputElement>>;
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  required?: boolean;
  error?: string;
  id?: string;
  innerRef?: RefObject<HTMLInputElement> | ((el: HTMLDivElement) => void);
  styles?: CSSProperties | IStyle;
  draggable?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  dataTestId?: string;
  maxLength?: number;
  placeholder?: string;
  onFocus?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  noWrapError?: boolean;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement> | undefined;
}

interface IFormNumberFieldProps extends IFormTextFieldProps {
  unit?: string;
  disabled?: boolean;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement> | undefined;
}

interface IMultilineTextFieldProps extends IFormTextFieldProps {
  rows?: number;
  placeholder?: string;
  chips?: string[];
  onChipClick?: (chip: string) => void;
  showChipsCallback?: (showChips: boolean) => void;
  disableResize?: boolean;
}

interface IDebouncedFormTextFieldProps {
  label?: string;
  onDebouncedChange: (value: string | number) => void;
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  debounceTime?: number;
  defaultValue?: string;
  required?: boolean;
  error?: string;
  id?: string;
  innerRef?: RefObject<HTMLInputElement> | ((el: HTMLDivElement) => void);
  styles?: CSSProperties | IStyle;
  draggable?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  dataTestId?: string;
  placeholder?: string;
  unit?: string;
}

interface IDebouncedFormNumberField
  extends Omit<IDebouncedFormTextFieldProps, 'onDebouncedChange' | 'defaultValue'> {
  onDebouncedChange: (value: string | number) => void;
  defaultValue?: number;
}

interface IDebouncedMultilineTextFieldProps extends IDebouncedFormTextFieldProps {
  rows?: number;
}

const TextInput = styled(InputBase)<{ disableResize?: boolean }>(({ disableResize }) => ({
  'label + &': {
    marginTop: '24px'
  },
  '& .MuiInputBase-input': {
    borderRadius: '6px',
    position: 'relative',
    backgroundColor: '#FFFFFF',
    color: '#333333',
    border: '1px solid #DDDDDD',
    fontSize: '16px',
    padding: '6px 10px',
    margin: '3px 0',
    fontFamily: 'Source Sans Pro, sans-serif',
    transition: 'all 0.16s ease-in-out',
    '&:focus': {
      boxShadow: `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 2px`,
      borderColor: theme.palette.primary.main
    },
    '&.MuiInputBase-inputMultiline': {
      transition: 'border-color 0.16s ease-in-out, box-shadow 0.16s ease-in-out',
      resize: disableResize ? 'none' : 'vertical',
      overflowY: 'auto',
      willChange: 'height',
      minHeight: '1.5em'
    }
  }
}));

export function FormTextField({
  label,
  value,
  onChange,
  onBlur,
  required,
  error,
  id,
  innerRef,
  styles,
  draggable,
  helperText,
  fullWidth = false,
  dataTestId,
  maxLength,
  placeholder,
  onFocus,
  noWrapError,
  onKeyDown
}: IFormTextFieldProps) {
  return (
    <FormControl variant="standard" sx={{ width: fullWidth ? '100%' : undefined }}>
      {label && (
        <InputLabel shrink htmlFor="text-input" sx={sharedClasses.inputLabel}>
          {label}
          {required && <span>*</span>}
        </InputLabel>
      )}
      <TextInput
        inputProps={{
          'data-testid': dataTestId,
          maxLength: maxLength
        }}
        disabled={draggable}
        sx={styles}
        value={value}
        id={id}
        onChange={onChange}
        onBlur={onBlur}
        inputRef={innerRef}
        placeholder={placeholder}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
      />
      {error && (
        <Box sx={{ ...sharedClasses.errorBox, textWrap: noWrapError ? 'nowrap' : 'wrap' }}>
          {error}
        </Box>
      )}
      {helperText && (
        <FormHelperText sx={{ marginLeft: '4px', fontFamily: 'Source Sans Pro' }}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
}

export function FormNumberField({
  label,
  value,
  onChange,
  required,
  error,
  id,
  innerRef,
  styles,
  unit,
  fullWidth,
  onKeyDown,
  placeholder,
  disabled
}: IFormNumberFieldProps) {
  return (
    <FormControl variant="standard" sx={{ width: '100%' }}>
      {label && (
        <InputLabel shrink htmlFor="text-input" sx={sharedClasses.inputLabel}>
          {label}
          {required && <span>*</span>}
        </InputLabel>
      )}
      <Stack
        sx={{
          flexDirection: 'row',
          alignItems: 'center',
          marginTop: 3,
          columnGap: 1,
          color: '#838383'
        }}
      >
        <TextInput
          type="number"
          sx={styles}
          value={value}
          id={id}
          onChange={onChange}
          inputRef={innerRef}
          fullWidth={fullWidth}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          disabled={disabled}
        />
        {unit}
      </Stack>
      {error && <Box sx={sharedClasses.errorBox}>{error}</Box>}
    </FormControl>
  );
}

export function FormTextFieldBlur({
  label,
  value,
  onChange,
  required,
  onBlur,
  error,
  id,
  innerRef,
  styles
}: IFormTextFieldProps) {
  return (
    <FormControl variant="standard">
      {label && (
        <InputLabel shrink htmlFor="text-input" sx={sharedClasses.inputLabel}>
          {label}
          {required && <span>*</span>}
        </InputLabel>
      )}
      <TextInput
        sx={styles}
        value={value}
        onBlur={onBlur}
        id={id}
        onChange={onChange}
        inputRef={innerRef}
      />
      {error && <Box sx={sharedClasses.errorBox}>{error}</Box>}
    </FormControl>
  );
}

export function DebouncedFormTextField({
  label,
  defaultValue,
  onDebouncedChange,
  required,
  error,
  debounceTime,
  id,
  innerRef,
  styles,
  helperText,
  fullWidth = false,
  dataTestId
}: IDebouncedFormTextFieldProps) {
  return (
    <DebounceInput
      defaultValue={defaultValue}
      onDebouncedChange={onDebouncedChange}
      debounceTime={debounceTime}
    >
      {({ value, onChange }) => (
        <FormTextField
          dataTestId={dataTestId}
          label={label}
          value={value}
          onChange={onChange}
          required={required}
          error={error}
          id={id}
          innerRef={innerRef}
          styles={styles}
          helperText={helperText}
          fullWidth={fullWidth}
        />
      )}
    </DebounceInput>
  );
}

export function DebouncedFormNumberField({
  label,
  defaultValue,
  onDebouncedChange,
  required,
  error,
  debounceTime,
  id,
  innerRef,
  styles,
  dataTestId,
  unit,
  fullWidth
}: IDebouncedFormNumberField) {
  return (
    <DebounceInput
      defaultValue={defaultValue}
      onDebouncedChange={onDebouncedChange}
      debounceTime={debounceTime}
    >
      {({ value, onChange }) => (
        <FormNumberField
          dataTestId={dataTestId}
          label={label}
          value={value as number}
          onChange={onChange}
          required={required}
          error={error}
          id={id}
          innerRef={innerRef}
          styles={styles}
          unit={unit}
          fullWidth={fullWidth}
        />
      )}
    </DebounceInput>
  );
}

export function DebouncedFormTextFieldBlur({
  label,
  defaultValue,
  onDebouncedChange,
  onBlur,
  required,
  error,
  debounceTime,
  id,
  innerRef,
  styles,
  dataTestId
}: IDebouncedFormTextFieldProps) {
  return (
    <DebounceInput
      defaultValue={defaultValue}
      onDebouncedChange={onDebouncedChange}
      debounceTime={debounceTime}
    >
      {({ value, onChange }) => (
        <FormTextFieldBlur
          label={label}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          required={required}
          error={error}
          id={id}
          dataTestId={dataTestId}
          innerRef={innerRef}
          styles={styles}
        />
      )}
    </DebounceInput>
  );
}

export function MultilineFormTextField({
  label,
  value,
  onChange,
  required,
  error,
  id,
  innerRef,
  styles,
  rows,
  helperText,
  draggable = false,
  dataTestId,
  fullWidth = false,
  placeholder,
  maxLength,
  chips,
  onChipClick,
  showChipsCallback,
  disableResize = false,
  ...rest
}: IMultilineTextFieldProps) {
  const [showChips, setShowChips] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const shouldShowChips = showChips && (!value || !isFocused);

  return (
    <FormControl variant="standard" sx={{ width: fullWidth ? '100%' : undefined }}>
      {label && (
        <InputLabel shrink htmlFor="text-input" sx={sharedClasses.inputLabel}>
          {label}
          {required && <span>*</span>}
        </InputLabel>
      )}
      <Box
        sx={{
          position: 'relative',
          marginTop: label ? theme.spacing(3) : undefined
        }}
      >
        <TextInput
          id={id}
          inputProps={{
            'data-testid': dataTestId,
            maxLength: maxLength
          }}
          multiline
          rows={rows || 6}
          required={required}
          fullWidth
          value={value}
          inputRef={innerRef}
          onChange={onChange}
          disableResize={disableResize}
          sx={{
            ...styles,
            '& .MuiInputBase-input': {
              paddingBottom: shouldShowChips ? '60px' : undefined,
              minHeight: shouldShowChips ? '100px' : 'auto',
              transition: 'padding-bottom 0.3s ease, min-height 0.3s ease'
            }
          }}
          disabled={draggable}
          placeholder={placeholder}
          onFocus={() => {
            if (chips && chips.length) {
              setIsFocused(true);
              setShowChips(true);
              showChipsCallback?.(true);
            }
          }}
          onBlur={() => {
            setTimeout(() => {
              setIsFocused(false);
              showChipsCallback?.(false);
              setShowChips(false);
            }, 400);
          }}
          {...rest}
        />

        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            margin: '8px',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            borderBottomLeftRadius: '6px',
            borderBottomRightRadius: '6px',
            zIndex: 1,
            pointerEvents: shouldShowChips ? 'auto' : 'none',
            transform: shouldShowChips ? 'translateY(0)' : 'translateY(10px)',
            opacity: shouldShowChips ? 1 : 0,
            transition: 'transform 0.3s ease, opacity 0.3s ease'
          }}
        >
          {chips?.map((chip, index) => (
            <Chip
              key={index}
              label={chip}
              onClick={() => onChipClick?.(chip)}
              size="small"
              sx={{
                backgroundColor: '#EDF2F5',
                color: '#084D6D',
                '&:hover': {
                  backgroundColor: '#E0E7EB'
                },
                cursor: 'pointer'
              }}
            />
          ))}
        </Box>
      </Box>
      {error && <Box sx={sharedClasses.errorBox}>{error}</Box>}
      {helperText && <FormHelperText sx={{ marginLeft: '4px' }}>{helperText}</FormHelperText>}
    </FormControl>
  );
}

export function MultilineDebouncedFormTextField({
  label,
  defaultValue,
  onDebouncedChange,
  debounceTime,
  required,
  error,
  id,
  innerRef,
  styles,
  rows,
  fullWidth,
  placeholder
}: IDebouncedMultilineTextFieldProps) {
  return (
    <DebounceInput
      defaultValue={defaultValue}
      onDebouncedChange={onDebouncedChange}
      debounceTime={debounceTime}
    >
      {({ value, onChange }) => (
        <MultilineFormTextField
          label={label}
          value={value}
          onChange={onChange}
          required={required}
          error={error}
          id={id}
          innerRef={innerRef}
          styles={styles}
          rows={rows}
          fullWidth={fullWidth}
          placeholder={placeholder}
        />
      )}
    </DebounceInput>
  );
}
